import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import OverviewTab from "@/pages/admin/OverviewTab";
import VehiclesTab from "@/pages/admin/VehiclesTab";
import BlackoutsTab from "@/pages/admin/BlackoutsTab";
import BookingsTab from "@/pages/admin/BookingsTab";
import EmailsTab from "@/pages/admin/EmailsTab";
import LicensesTab from "@/pages/admin/LicensesTab";
import CouponsTab from "@/pages/admin/CouponsTab";
import LeadsTab from "@/pages/admin/LeadsTab";
import UsersTab from "@/pages/admin/UsersTab";
import MaintenanceTab from "@/pages/admin/MaintenanceTab";
import LoyaltyTab from "@/pages/admin/LoyaltyTab";
import LocationsTab from "@/pages/admin/LocationsTab";
import GalleryTab from "@/pages/admin/GalleryTab";
import PaymentsTab from "@/pages/admin/PaymentsTab";
import TokensTab from "@/pages/admin/TokensTab";
import ReviewsTab from "@/pages/admin/ReviewsTab";
import PricingTab from "@/pages/admin/PricingTab";
import VisitorsTab from "@/pages/admin/VisitorsTab";
import SeoTab from "@/pages/admin/SeoTab";
import ContractsTab from "@/pages/admin/ContractsTab";
import QuotesTab from "@/pages/admin/QuotesTab";
import {
  LayoutDashboard, Car, ClipboardList, CalendarX2, FileText, Tag,
  Inbox, Users, Crown, MapPin, Wrench, Mail, Images, CreditCard, Key, Star, TrendingUp, Globe, Search, FileSignature,
} from "lucide-react";

const TABS = [
  { value: "overview", label: "Overview", icon: LayoutDashboard, testid: "admin-tab-overview" },
  { value: "vehicles", label: "Vehicles", icon: Car, testid: "admin-tab-vehicles" },
  { value: "bookings", label: "Bookings", icon: ClipboardList, testid: "admin-tab-bookings" },
  { value: "quotes", label: "Quotes", icon: FileText, testid: "admin-tab-quotes" },
  { value: "payments", label: "Payments", icon: CreditCard, testid: "admin-tab-payments" },
  { value: "blackouts", label: "Blackout Dates", icon: CalendarX2, testid: "admin-tab-blackouts" },
  { value: "licenses", label: "Licenses", icon: FileText, testid: "admin-tab-licenses" },
  { value: "contracts", label: "Contracts", icon: FileSignature, testid: "admin-tab-contracts" },
  { value: "coupons", label: "Discount Codes", icon: Tag, testid: "admin-tab-coupons" },
  { value: "leads", label: "Leads", icon: Inbox, testid: "admin-tab-leads" },
  { value: "users", label: "Users", icon: Users, testid: "admin-tab-users" },
  { value: "loyalty", label: "Loyalty", icon: Crown, testid: "admin-tab-loyalty" },
  { value: "locations", label: "GPS / Locations", icon: MapPin, testid: "admin-tab-locations" },
  { value: "maintenance", label: "Maintenance", icon: Wrench, testid: "admin-tab-maintenance" },
  { value: "emails", label: "Emails", icon: Mail, testid: "admin-tab-emails" },
  { value: "gallery", label: "Gallery", icon: Images, testid: "admin-tab-gallery" },
  { value: "tokens", label: "API Tokens", icon: Key, testid: "admin-tab-tokens" },
  { value: "reviews", label: "Reviews", icon: Star, testid: "admin-tab-reviews" },
  { value: "pricing", label: "Smart Pricing", icon: TrendingUp, testid: "admin-tab-pricing" },
  { value: "visitors", label: "Visitors", icon: Globe, testid: "admin-tab-visitors" },
  { value: "seo", label: "SEO & Verification", icon: Search, testid: "admin-tab-seo" },
];

const TAB_GROUPS = [
  { label: null, tabs: ["overview"] },
  { label: "Operations", tabs: ["bookings", "quotes", "vehicles", "blackouts", "licenses", "contracts", "locations", "maintenance"] },
  { label: "Finance", tabs: ["payments", "pricing", "coupons"] },
  { label: "Marketing", tabs: ["loyalty", "reviews", "leads", "gallery"] },
  { label: "System", tabs: ["users", "tokens", "emails", "visitors", "seo"] },
];

const TIER_TABS = {
  supervisor: ["overview", "bookings", "quotes", "vehicles", "blackouts", "licenses", "contracts", "locations", "maintenance", "coupons", "leads", "loyalty", "gallery", "reviews", "emails", "visitors"],
  employee: ["overview", "bookings", "quotes", "vehicles", "blackouts", "licenses", "contracts", "locations", "maintenance"],
};

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "overview");
  const focusBooking = searchParams.get("booking") || "";

  const allowed = user && user.role === "admin" && !user.staff_role ? null : (Array.isArray(user?.tab_overrides) ? user.tab_overrides : (TIER_TABS[user?.staff_role] || TIER_TABS.employee));
  const visibleTabs = allowed ? TABS.filter((t) => allowed.includes(t.value)) : TABS;
  const tabAllowed = visibleTabs.some((t) => t.value === tab);

  useEffect(() => {
    if (visibleTabs.length && !tabAllowed) setTab(visibleTabs[0].value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user]);

  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setTab(t);
  }, [searchParams]);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/login");
  }, [user, loading, navigate]);

  if (loading || !user || user.role !== "admin")
    return <div className="pt-40 pb-24 text-center text-muted-foreground font-ui">Loading…</div>;

  return (
    <div data-testid="admin-panel" className="min-h-screen bg-[#FAF6F0] pt-28">
      <Tabs value={tab} onValueChange={setTab} orientation="vertical" className="flex flex-col md:flex-row">
        <aside className="md:w-64 shrink-0 bg-white border-b md:border-b-0 md:border-r border-[#E5DED5] md:sticky md:top-28 md:h-[calc(100vh-7rem)] md:self-start z-40 md:flex md:flex-col">
          <div className="hidden md:flex flex-col px-5 pt-7 pb-6 border-b border-[#E5DED5] shrink-0">
            <img src="/logo.png" alt="Easy Drive Car Rental" data-testid="admin-logo" className="h-14 w-auto object-contain self-start" />
            <p className="mt-4 text-[10px] font-ui tracking-[0.3em] uppercase text-[#C71E2E]">Admin Panel</p>
            <p className="mt-1 text-xs font-ui text-[#4A4A4A]">Fleet, bookings & site management</p>
          </div>
          <TabsList className="flex flex-row md:flex-col justify-start items-stretch overflow-x-auto md:overflow-x-visible md:overflow-y-auto md:flex-1 h-auto w-full bg-transparent p-3 md:p-4 gap-1.5 rounded-none">
            {TAB_GROUPS.map((g) => {
              const tabs = g.tabs.map((v) => visibleTabs.find((t) => t.value === v)).filter(Boolean);
              if (!tabs.length) return null;
              return (
                <div key={g.label || "top"} className="contents md:block md:space-y-1">
                  {g.label && <p className="hidden md:block px-3.5 pt-3 pb-1 text-[9px] font-ui tracking-[0.25em] uppercase text-[#9B8F80]">{g.label}</p>}
                  {tabs.map(({ value, label, icon: Icon, testid }) => (
                    <TabsTrigger
                      key={value}
                      data-testid={testid}
                      value={value}
                      className="flex items-center justify-start gap-2.5 px-3.5 py-2.5 rounded-xl text-sm font-ui whitespace-nowrap text-[#4A4A4A] hover:bg-[#F5EFE6] hover:text-[#1A1A1A] data-[state=active]:bg-[#C71E2E]/10 data-[state=active]:text-[#C71E2E] data-[state=active]:font-semibold data-[state=active]:shadow-none transition-colors duration-200 md:w-full"
                    >
                      <Icon size={15} className="shrink-0" />
                      {label}
                    </TabsTrigger>
                  ))}
                </div>
              );
            })}
          </TabsList>
          <div className="hidden md:block px-5 py-4 border-t border-[#E5DED5] shrink-0">
            <p data-testid="admin-user-email" className="text-[11px] font-ui text-[#4A4A4A] truncate">{user.email}</p>
            <p data-testid="admin-user-role" className="mt-0.5 text-[9px] font-ui tracking-[0.2em] uppercase text-[#C71E2E] font-semibold">{user.staff_role || "Owner"}</p>
          </div>
        </aside>

        <div className="flex-1 min-w-0 px-4 sm:px-6 lg:px-10 py-8 md:py-10">
          {tabAllowed ? (
          <>
          <TabsContent value="overview" className="mt-0"><OverviewTab /></TabsContent>
          <TabsContent value="vehicles" className="mt-0"><VehiclesTab /></TabsContent>
          <TabsContent value="bookings" className="mt-0"><BookingsTab focusBooking={focusBooking} /></TabsContent>
          <TabsContent value="quotes" className="mt-0"><QuotesTab /></TabsContent>
          <TabsContent value="payments" className="mt-0"><PaymentsTab /></TabsContent>
          <TabsContent value="blackouts" className="mt-0"><BlackoutsTab /></TabsContent>
          <TabsContent value="licenses" className="mt-0"><LicensesTab /></TabsContent>
          <TabsContent value="contracts" className="mt-0"><ContractsTab /></TabsContent>
          <TabsContent value="coupons" className="mt-0"><CouponsTab /></TabsContent>
          <TabsContent value="leads" className="mt-0"><LeadsTab /></TabsContent>
          <TabsContent value="users" className="mt-0"><UsersTab /></TabsContent>
          <TabsContent value="loyalty" className="mt-0"><LoyaltyTab /></TabsContent>
          <TabsContent value="locations" className="mt-0"><LocationsTab /></TabsContent>
          <TabsContent value="maintenance" className="mt-0"><MaintenanceTab /></TabsContent>
          <TabsContent value="emails" className="mt-0"><EmailsTab /></TabsContent>
          <TabsContent value="gallery" className="mt-0"><GalleryTab /></TabsContent>
          <TabsContent value="tokens" className="mt-0"><TokensTab /></TabsContent>
          <TabsContent value="reviews" className="mt-0"><ReviewsTab /></TabsContent>
          <TabsContent value="pricing" className="mt-0"><PricingTab /></TabsContent>
          <TabsContent value="visitors" className="mt-0"><VisitorsTab /></TabsContent>
          <TabsContent value="seo" className="mt-0"><SeoTab /></TabsContent>
          </>
          ) : (
            <p data-testid="admin-tab-blocked" className="text-sm font-ui text-muted-foreground">Your role doesn't include this section.</p>
          )}
        </div>
      </Tabs>
    </div>
  );
}
